/**
 * Run parser — converts <w:r> elements to ProseMirror text nodes with marks.
 *
 * A "run" in OOXML is a contiguous span of text with the same formatting.
 * Each <w:r> contains:
 *   - <w:rPr> (run properties: bold, italic, etc.)
 *   - <w:t> (text content)
 *   - <w:br> (line/page break)
 *   - <w:tab> (tab character)
 *   - <w:sym> (symbol)
 */

import { PMNode, PMMark, RunProperties, ParseContext } from './types';
import { getChildNS, parseRunPropertiesFromElement, W_NS } from './zipReader';
import { CommentTracker } from './commentParser';

/**
 * Parse a single <w:r> element into one or more ProseMirror nodes.
 *
 * Usually produces a single text node, but may produce multiple nodes
 * when the run contains mixed content (text + breaks).
 */
export function parseRun(runEl: Element, ctx: ParseContext): PMNode[] {
  const nodes: PMNode[] = [];

  // Parse run properties
  const rPrEl = getChildNS(runEl, W_NS, 'rPr');
  const runProps = rPrEl ? parseRunPropertiesFromElement(rPrEl) : {};
  const marks = runPropertiesToMarks(runProps);

  // Iterate through child elements
  const children = runEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue; // skip non-elements
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    switch (localName) {
      case 't': {
        // <w:t> — text content
        const text = el.textContent || '';
        if (text.length > 0) {
          const textNode: PMNode = { type: 'text', text };
          if (marks.length > 0) textNode.marks = marks;
          nodes.push(textNode);
        }
        break;
      }

      case 'br': {
        // <w:br> — break
        const brType =
          el.getAttributeNS(W_NS, 'type') || el.getAttribute('w:type');
        if (brType === 'page') {
          // Page break → pageBreak node
          nodes.push({ type: 'pageBreak' });
        } else {
          // Line break → hardBreak node
          nodes.push({ type: 'hardBreak' });
        }
        break;
      }

      case 'tab': {
        // <w:tab> — tab character (render as spaces in ProseMirror)
        const tabNode: PMNode = { type: 'text', text: '\t' };
        if (marks.length > 0) tabNode.marks = marks;
        nodes.push(tabNode);
        break;
      }

      case 'sym': {
        // <w:sym> — symbol character
        const charCode =
          el.getAttributeNS(W_NS, 'char') || el.getAttribute('w:char');
        if (charCode) {
          const char = String.fromCharCode(parseInt(charCode, 16));
          const symNode: PMNode = { type: 'text', text: char };
          if (marks.length > 0) symNode.marks = marks;
          nodes.push(symNode);
        }
        break;
      }

      case 'drawing': {
        // <w:drawing> — image (DrawingML)
        // Store the raw element for async resolution in the image parser
        nodes.push({ type: '_drawing', attrs: { _element: el } });
        break;
      }

      case 'pict': {
        // <w:pict> — image (VML legacy)
        nodes.push({ type: '_pict', attrs: { _element: el } });
        break;
      }

      case 'commentReference': {
        // <w:commentReference w:id="N"/> — register point comment
        const commentTracker = ctx.commentTracker as CommentTracker | undefined;
        if (commentTracker) {
          const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
          if (id) commentTracker.handleCommentReference(id);
        }
        break;
      }

      // rPr is already handled above — skip it
      case 'rPr':
        break;

      default:
        // Unknown run child — skip silently
        break;
    }
  }

  return nodes;
}

/**
 * Convert RunProperties to an array of ProseMirror marks.
 */
export function runPropertiesToMarks(props: RunProperties): PMMark[] {
  const marks: PMMark[] = [];

  if (props.bold) marks.push({ type: 'bold' });
  if (props.italic) marks.push({ type: 'italic' });
  if (props.underline) marks.push({ type: 'underline' });
  if (props.strike) marks.push({ type: 'strike' });
  if (props.subscript) marks.push({ type: 'subscript' });
  if (props.superscript) marks.push({ type: 'superscript' });

  if (props.color) {
    marks.push({ type: 'textStyle', attrs: { color: `#${props.color}` } });
  }

  if (props.highlight) {
    // TipTap uses textStyle mark with backgroundColor attr (via BackgroundColor extension),
    // not a separate "highlight" mark type
    marks.push({
      type: 'textStyle',
      attrs: { backgroundColor: props.highlight },
    });
  }

  if (props.fontSize) {
    marks.push({
      type: 'textStyle',
      attrs: { fontSize: `${props.fontSize}pt` },
    });
  }

  if (props.fontFamily) {
    marks.push({
      type: 'textStyle',
      attrs: { fontFamily: props.fontFamily },
    });
  }

  // Merge multiple textStyle marks into one
  return mergeTextStyleMarks(marks);
}

/**
 * Merge multiple textStyle marks into a single mark with combined attrs.
 * TipTap's TextStyle extension expects a single textStyle mark per text node.
 */
function mergeTextStyleMarks(marks: PMMark[]): PMMark[] {
  const textStyleMarks = marks.filter((m) => m.type === 'textStyle');
  const otherMarks = marks.filter((m) => m.type !== 'textStyle');

  if (textStyleMarks.length <= 1) return marks;

  // Merge all textStyle attrs into one mark
  const mergedAttrs: Record<string, unknown> = {};
  for (const m of textStyleMarks) {
    if (m.attrs) Object.assign(mergedAttrs, m.attrs);
  }

  return [...otherMarks, { type: 'textStyle', attrs: mergedAttrs }];
}
